import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { User, Mail, Phone, Building2, Briefcase, Shield, KeyRound, Save, Loader2, CheckCircle2, AlertTriangle, FolderKanban, Tv, Bell } from 'lucide-react';

/**
 * ClientProfilePage — built from scratch for clients only.
 *
 *  Clients are at the top of the food chain. Their profile is NOT a
 *  rehash of the employee profile (which has Documents/Leave/Performance,
 *  KYC fields, Aadhar, Bank Account, etc.).
 *
 *  This page intentionally shows ONLY:
 *    1. Identity (name, email, phone, organization, role badge)
 *    2. Project access (what shows + episodes they can see)
 *    3. Security (change password)
 *
 *  Nothing else. No tasks, no earnings, no leave, no performance.
 */
export default function ClientProfilePage() {
  const { api, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [saveOK, setSaveOK] = useState('');

  // Project access
  const [projects, setProjects] = useState([]);

  // Notification preferences
  const [notifMode, setNotifMode] = useState('twice_daily');
  const [savingNotif, setSavingNotif] = useState(false);
  const [queuedCount, setQueuedCount] = useState(0);

  // Password change
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/profile/${user.id}`).then(r => {
      setProfile(r.data);
      setEditForm({
        full_name: r.data.full_name || '',
        phone: r.data.phone || '',
        company_name: r.data.company_name || r.data.organization || '',
        designation: r.data.designation || r.data.title || '',
      });
    }).catch(() => {});
    api.get('/projects').then(r => setProjects(r.data || [])).catch(() => {});
    api.get('/notifications/prefs').then(r => setNotifMode(r.data?.mode || 'twice_daily')).catch(() => {});
    api.get('/notifications/queue/preview').then(r => setQueuedCount(r.data?.count || 0)).catch(() => {});
  }, [user?.id]);

  const updateNotifMode = async (newMode) => {
    setSavingNotif(true);
    try {
      await api.put('/notifications/prefs', { mode: newMode });
      setNotifMode(newMode);
    } catch (e) { /* ignore */ }
    finally { setSavingNotif(false); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    setSaveOK('');
    try {
      await api.put('/profile/update', editForm);
      setProfile(p => ({ ...p, ...editForm }));
      setEditing(false);
      setSaveOK('Profile updated');
      setTimeout(() => setSaveOK(''), 3000);
    } catch (e) {
      setSaveOK('Save failed: ' + (e?.response?.data?.detail || 'unknown'));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    setPwErr(''); setPwMsg('');
    if (!currentPw || !newPw) { setPwErr('Both fields required'); return; }
    if (newPw.length < 6) { setPwErr('New password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { setPwErr('New passwords do not match'); return; }
    setPwBusy(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPw,
        new_password: newPw,
      });
      setPwMsg('Password updated successfully');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => { setPwOpen(false); setPwMsg(''); }, 1500);
    } catch (e) {
      setPwErr(e?.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwBusy(false);
    }
  };

  if (!profile) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-zinc-500" data-testid="client-profile-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading account…
      </div>
    );
  }

  // Build a clean Show -> Episodes tree for the project access card
  const shows = projects.filter(p => p.is_show);
  const episodesByShow = new Map();
  for (const ep of projects.filter(p => p.parent_project_id)) {
    const arr = episodesByShow.get(ep.parent_project_id) || [];
    arr.push(ep);
    episodesByShow.set(ep.parent_project_id, arr);
  }
  const standalone = projects.filter(p => !p.is_show && !p.parent_project_id);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6 max-w-5xl mx-auto" data-testid="client-profile">
      {/* ── HEADER ── */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white tracking-tight">My Account</h1>
        <p className="text-sm text-zinc-400 mt-1">Manage your contact details, organization info and password.</p>
      </div>

      {/* ── IDENTITY CARD ── */}
      <div className="rounded-xl border border-zinc-800 bg-gradient-to-br from-violet-500/5 via-zinc-900 to-zinc-950 p-6 mb-5" data-testid="profile-identity">
        <div className="flex items-start gap-5">
          <div className="h-20 w-20 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center text-2xl font-bold text-white flex-shrink-0">
            {(profile.full_name || profile.email || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-white truncate">{profile.full_name || profile.email}</h2>
              <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-violet-500/20 text-violet-200 border border-violet-500/40" data-testid="role-badge">
                {profile.role === 'client_director' ? 'Client Director' : 'Client Producer'}
              </span>
            </div>
            {!editing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2.5 gap-x-6 mt-3 text-sm">
                <Field icon={Mail} label="Email" value={profile.email} mono />
                <Field icon={Phone} label="Phone" value={profile.phone || '—'} />
                <Field icon={Building2} label="Organization" value={profile.company_name || profile.organization || '—'} />
                <Field icon={Briefcase} label="Designation" value={profile.designation || profile.title || '—'} />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <Input label="Full Name" value={editForm.full_name} onChange={v => setEditForm(f => ({ ...f, full_name: v }))} testid="input-fullname" />
                <Input label="Phone" value={editForm.phone} onChange={v => setEditForm(f => ({ ...f, phone: v }))} testid="input-phone" />
                <Input label="Organization" value={editForm.company_name} onChange={v => setEditForm(f => ({ ...f, company_name: v }))} testid="input-org" />
                <Input label="Designation / Title" value={editForm.designation} onChange={v => setEditForm(f => ({ ...f, designation: v }))} testid="input-title" />
              </div>
            )}
            <div className="flex items-center gap-2 mt-4">
              {!editing ? (
                <button onClick={() => setEditing(true)}
                  data-testid="edit-profile-btn"
                  className="h-8 px-4 rounded-md bg-violet-500 hover:bg-violet-400 text-white text-xs font-semibold">
                  Edit details
                </button>
              ) : (
                <>
                  <button onClick={saveProfile} disabled={savingProfile}
                    data-testid="save-profile-btn"
                    className="h-8 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold flex items-center gap-2 disabled:opacity-50">
                    {savingProfile ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    {savingProfile ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => setEditing(false)}
                    className="h-8 px-4 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">
                    Cancel
                  </button>
                </>
              )}
              {saveOK && (
                <span className={`text-[11px] flex items-center gap-1 ${saveOK.startsWith('Save failed') ? 'text-rose-300' : 'text-emerald-300'}`} data-testid="save-feedback">
                  <CheckCircle2 className="w-3 h-3" /> {saveOK}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── PROJECT ACCESS ── */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-6 mb-5" data-testid="profile-projects">
        <div className="flex items-center gap-2 mb-3">
          <FolderKanban className="w-4 h-4 text-cyan-300" />
          <h3 className="text-sm font-bold text-white">Your Project Access</h3>
          <span className="text-[10px] text-zinc-500 ml-1">({projects.length} project{projects.length !== 1 ? 's' : ''})</span>
        </div>
        {projects.length === 0 ? (
          <p className="text-xs text-zinc-500">You don't have any projects assigned yet.</p>
        ) : (
          <div className="space-y-2.5">
            {standalone.map(p => (
              <div key={p.id} className="flex items-center gap-2.5 px-3 py-2 rounded-md border border-zinc-800 bg-zinc-950/40 text-xs"
                   data-testid={`profile-project-${p.id}`}>
                <FolderKanban className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                <span className="text-zinc-200 truncate">{p.name}</span>
              </div>
            ))}
            {shows.map(show => {
              const eps = episodesByShow.get(show.id) || [];
              return (
                <div key={show.id} className="rounded-md border border-zinc-800 bg-zinc-950/40 overflow-hidden" data-testid={`profile-show-${show.id}`}>
                  <div className="px-3 py-2 flex items-center gap-2.5 bg-violet-500/5">
                    <Tv className="w-3.5 h-3.5 text-violet-300 flex-shrink-0" />
                    <span className="text-violet-100 font-semibold text-xs truncate">{show.name}</span>
                    <span className="ml-auto text-[10px] text-zinc-500">{eps.length} episode{eps.length !== 1 ? 's' : ''}</span>
                  </div>
                  {eps.length > 0 && (
                    <ul className="divide-y divide-zinc-800/50">
                      {eps.map(ep => (
                        <li key={ep.id} className="px-3 py-1.5 pl-9 text-[11px] text-zinc-300 flex items-center gap-2">
                          <span className="text-[9px] font-mono text-zinc-500">EP{String(ep.episode_number ?? '').padStart(2, '0')}</span>
                          <span className="truncate">{ep.name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── NOTIFICATIONS ── */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-6 mb-5" data-testid="profile-notifications">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-300" />
            <h3 className="text-sm font-bold text-white">Notification Preferences</h3>
          </div>
          {queuedCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300" data-testid="queued-count">
              {queuedCount} update{queuedCount !== 1 ? 's' : ''} waiting in next digest
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
          New submissions ready for your review will always email you instantly. Everything else (your decisions, comments, status updates) is batched into a clean daily summary so we never spam you.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          {[
            { k: 'twice_daily', l: 'Twice Daily',     d: '1 PM + 7 PM IST' },
            { k: 'daily',       l: 'Once Daily',      d: '7 PM IST EOD' },
            { k: 'instant',     l: 'Real-time',       d: 'Every event (max 6/day)' },
            { k: 'off',         l: 'Off',             d: 'Dashboard only' },
          ].map(opt => {
            const active = notifMode === opt.k;
            return (
              <button key={opt.k}
                onClick={() => updateNotifMode(opt.k)}
                disabled={savingNotif}
                data-testid={`notif-mode-${opt.k}`}
                className={`text-left px-3 py-2.5 rounded-md border transition disabled:opacity-50 ${
                  active
                    ? 'border-amber-500/60 bg-amber-500/10 text-amber-200'
                    : 'border-zinc-800 bg-zinc-950/40 text-zinc-300 hover:border-zinc-700'
                }`}>
                <div className="flex items-center gap-1.5 text-xs font-semibold">
                  {active && <CheckCircle2 className="w-3 h-3" />}
                  {opt.l}
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">{opt.d}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── SECURITY ── */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-6" data-testid="profile-security">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-300" />
            <h3 className="text-sm font-bold text-white">Security</h3>
          </div>
          {!pwOpen && (
            <button onClick={() => setPwOpen(true)}
              data-testid="change-pw-btn"
              className="h-8 px-4 rounded-md border border-zinc-700 hover:border-emerald-400/60 hover:bg-emerald-500/10 text-zinc-200 text-xs font-medium flex items-center gap-2">
              <KeyRound className="w-3 h-3" /> Change Password
            </button>
          )}
        </div>
        {pwOpen && (
          <div className="mt-4 space-y-3" data-testid="pw-form">
            <Input label="Current password" value={currentPw} onChange={setCurrentPw} type="password" testid="input-current-pw" />
            <Input label="New password (min 6 chars)" value={newPw} onChange={setNewPw} type="password" testid="input-new-pw" />
            <Input label="Confirm new password" value={confirmPw} onChange={setConfirmPw} type="password" testid="input-confirm-pw" />
            {pwErr && (
              <div className="text-[11px] text-rose-300 flex items-center gap-1.5" data-testid="pw-err">
                <AlertTriangle className="w-3 h-3" /> {pwErr}
              </div>
            )}
            {pwMsg && (
              <div className="text-[11px] text-emerald-300 flex items-center gap-1.5" data-testid="pw-msg">
                <CheckCircle2 className="w-3 h-3" /> {pwMsg}
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <button onClick={changePassword} disabled={pwBusy}
                data-testid="submit-pw-btn"
                className="h-8 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold flex items-center gap-2 disabled:opacity-50">
                {pwBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                {pwBusy ? 'Updating…' : 'Update password'}
              </button>
              <button onClick={() => { setPwOpen(false); setPwErr(''); setPwMsg(''); }}
                className="h-8 px-4 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-3.5 h-3.5 text-zinc-500 mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`text-zinc-200 text-sm truncate ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
    </div>
  </div>
);

const Input = ({ label, value, onChange, type = 'text', testid }) => (
  <div>
    <label className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1 block">{label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      data-testid={testid}
      className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm text-white focus:outline-none focus:border-violet-500/50" />
  </div>
);
